// `@primer-io/checkout-web` ships as CommonJS — a static import breaks Nuxt's
// prerender step. Dynamic import creates a lazy chunk; the Primer integration
// calls this early (on email set) so the chunk is cached by the time
// PrimerCheckout mounts.
//
// Failure handling: a rejected load is never cached — the singleton resets so
// every later call (checkout mount, reinit, the integration's online retry)
// re-attempts. A visitor who goes offline mid-pipe fails the chunk fetch the
// moment their email is set; caching that rejection would keep the checkout
// dead until a full page reload. Retrying the import alone is not enough
// either: Chrome and Firefox cache FAILED module fetches in the page's module
// map (fixed in the HTML spec 2026-07, whatwg/html#10327, not yet shipped in
// any engine), so a retried import() of the same chunk rejects instantly
// without touching the network. Script tags are exempt from that cache, so
// when the import fails we fall back to Primer's CDN build (`Primer.min.js`,
// same pinned version), which exposes the same namespace on `window.Primer`.
//
// Version resolved at build time by Vite — no runtime package.json read needed.
import primerPkg from '@primer-io/checkout-web/package.json'

// The subset of the npm module the app consumes (see usePrimer.ts). The CDN
// fallback can only provide this surface, so the exported type is narrowed to
// keep both load paths honest.
export type PrimerSdk = Pick<typeof import('@primer-io/checkout-web'), 'Primer' | 'PaymentFlow'>

export const PRIMER_CDN_BASE = `https://sdk.primer.io/web/v${primerPkg.version}`

// `window.Primer` as set by the CDN build — the same namespace surface as the
// npm module's `Primer` export (Primer.min.js ends with `window.Primer=n`
// where `n` carries showUniversalCheckout / createHeadless / ...).
type PrimerGlobal = PrimerSdk['Primer']

// PaymentFlow is a plain string enum in the npm package (dist/index.d.ts);
// the CDN build doesn't export it, so mirror its members for the fallback.
/* eslint-disable @typescript-eslint/naming-convention -- mirrors Primer's enum members */
const PAYMENT_FLOW_FALLBACK = {
  DEFAULT: 'DEFAULT',
  PREFER_VAULT: 'PREFER_VAULT',
} as PrimerSdk['PaymentFlow']
/* eslint-enable @typescript-eslint/naming-convention */

let primerSdkPromise: Promise<PrimerSdk> | null = null

const getGlobalPrimer = (): PrimerGlobal | null => {
  const g = (window as { Primer?: PrimerGlobal }).Primer
  return g && typeof g.showUniversalCheckout === 'function' ? g : null
}

const loadFromCdn = (): Promise<PrimerSdk> =>
  new Promise<PrimerSdk>((resolve, reject) => {
    // A previous fallback attempt (or anything else) may have already put the
    // SDK on the global — no second script tag needed then.
    const fromGlobal = getGlobalPrimer()
    if (fromGlobal) {
      resolve({ Primer: fromGlobal, PaymentFlow: PAYMENT_FLOW_FALLBACK })
      return
    }
    const script = document.createElement('script')
    script.src = `${PRIMER_CDN_BASE}/Primer.min.js`
    script.crossOrigin = 'anonymous'
    script.onload = () => {
      const primer = getGlobalPrimer()
      if (primer) {
        resolve({ Primer: primer, PaymentFlow: PAYMENT_FLOW_FALLBACK })
        return
      }
      script.remove()
      reject(new Error('Primer CDN script loaded but window.Primer is missing'))
    }
    script.onerror = () => {
      // Drop the failed tag so a retry injects a fresh one instead of
      // stacking dead elements.
      script.remove()
      reject(new Error(`Failed to load Primer SDK from CDN: ${script.src}`))
    }
    document.head.appendChild(script)
  })

export const loadPrimerSdk = (): Promise<PrimerSdk> => {
  if (!primerSdkPromise) {
    primerSdkPromise = import('@primer-io/checkout-web')
      .catch((importErr) => {
        // SSR has no DOM for the fallback (and no module-map poisoning either).
        if (typeof window === 'undefined') throw importErr
        console.warn(
          '[primer] SDK chunk import failed — falling back to the Primer CDN script',
          importErr,
        )
        return loadFromCdn()
      })
      .catch((err) => {
        // Both paths failed (still offline). Clear the singleton so the next
        // call re-attempts the whole chain.
        primerSdkPromise = null
        throw err
      })
  }
  return primerSdkPromise
}
